import { inject, Injectable } from '@angular/core';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { TopicRepository } from '../topic/topic.repository';
import { EntryRepository } from '../entry/entry.repository';
import dayjs from 'dayjs';
import { dateFormat } from '../../tools/date';
import { ActivatedRoute } from '@angular/router';
import { TopicAndEntryMaybe } from './topic-and-entry-maybe';
import { IndexBlockViewModel } from './index-block-view-model';
import { todayWithTolerance } from './today-with-tolerance';
import { sortTopics } from 'src/tools/sort-topics';

@Injectable({providedIn: 'root'})
export class DayService {
  private route = inject(ActivatedRoute);
  private topicRepository = inject(TopicRepository);
  private entryRepository = inject(EntryRepository);

  date$: Observable<string> = this.route.params.pipe(
    map(params => {
      const specificDate = params['date'];
      if (specificDate) {
        return specificDate;
      }
      return todayWithTolerance();
    })
  );

  entries$ = this.date$.pipe(
    switchMap(date => this.entryRepository.getEntriesByDate$(date))
  );

  trackerVms$: Observable<TopicAndEntryMaybe[]> =
    combineLatest([
      this.topicRepository.trackerTopics$,
      this.entries$
    ]).pipe(
      map(([
             topics,
             entries
           ]) => {
        return topics
          .map(topic => ({
            topic,
            entry: entries.find(entry =>
              entry.topic_id === topic.id
            )
          }))
      })
    );

  nonTrackerVms$: Observable<TopicAndEntryMaybe[]> = combineLatest([
    this.entries$,
    this.topicRepository.tempTopics$,
    this.topicRepository.topics$
  ]).pipe(
    map(([
           entries,
           tempTopicsIds,
           topics
         ]) => {
      let emptyPinnedTopics = topics.filter(x =>
        x.is_pinned
        && !entries.some(entry => entry.topic_id === x.id)
      );

      entries = entries.filter(x => {
          const topic = this.topicRepository.getTopic(x.topic_id);
          return !topic?.range_target || !topic.is_active_tracker
        });

      const tempTopics = topics.filter(
        topic => tempTopicsIds.includes(topic.id)
      );

      const shownTopics = [
        ...emptyPinnedTopics,
        ...tempTopics
      ]

      return [
        ...shownTopics.map(topic => ({
          topic,
          entry: entries.find(entry => entry.topic_id === topic.id)
        })),
        ...entries.map(entry => ({
          topic: topics.find(topic => topic.id === entry.topic_id)!,
          entry
        }))
      ].sort((a, b) => {
        const valA = !!a.topic.order_number ? a.topic.order_number : 9999;
        const valB = !!b.topic.order_number ? b.topic.order_number : 9999;
        return valA - valB
      });
    })
  );

  pinnedVms$ = this.nonTrackerVms$.pipe(
    map(entries => entries.filter(
      x => x.topic.is_pinned
    ))
  );

  otherVms$ = this.nonTrackerVms$.pipe(
    map(entries => entries.filter(
      x => !x.topic.is_pinned
    ))
  );

  suggestedTopics$ = this.topicRepository
    .standardTopics$.pipe(
      switchMap(topics => this.nonTrackerVms$.pipe(
        map(existingEntries => topics.filter(
          topic => !existingEntries.find(entry => entry.topic.id === topic.id)
        ).sort(sortTopics))
      ))
    );

  index$: Observable<IndexBlockViewModel[]> = combineLatest([
    this.trackerVms$,
    this.pinnedVms$,
    this.otherVms$
  ]).pipe(map(([
                 trackerEntries,
                 pinnedEntries,
                 otherEntries
               ]) => {
    return [
      {
        title: 'Tracker',
        icon: 'bar-chart',
        vms: trackerEntries
      },
      {
        title: 'Lesezeichen',
        icon: 'bookmark',
        vms: pinnedEntries
      },
      {
        title: 'Sonstige',
        icon: 'bulb',
        vms: otherEntries
      }
    ]
  }))

  constructor() {
    this.date$.subscribe(date => console.log('date from DayService*', date))
  }
}
